import { openDB } from "idb";
import CryptoJS from "crypto-js";
const DB_NAME = "myHsigcom";
// Numéro de schéma. Depuis la v9, une montée de version N'EST PLUS destructive :
// upgrade() ne crée que ce qui manque et forceDBVersionCheck() laisse migrer au
// lieu de purger. Une montée de version conserve donc les données locales, y
// compris les commandes de caisse en attente de synchronisation.
//
// ⚠️ Reste destructif dans un seul cas : une BAISSE de version, c'est-à-dire un
// poste servi par un bundle plus ancien que le schéma déjà présent (rollback,
// service worker périmé). IndexedDB refuse alors l'ouverture, et la purge est la
// seule issue. Ne jamais redescendre ce numéro.
//
// Pour ajouter un store : incrémenter, puis ajouter son ensureStore() dans
// upgrade(). Ne jamais modifier le keyPath d'un store existant (il faudrait
// le supprimer et le recréer, donc perdre son contenu).
//   6 → stores Scan & Go (sg*)
//   7 → stores du module Serveur (svIdentite, svCommandes)
//   8 → - //   9 → upgrade incrémental + stores orderQueueTrash et comptabilisationvente
//  10 → réparation : des postes sont passés en v9 SANS que upgrade() ne tourne,
//       donc sans les stores de la v9. Cause : un openDB(DB_NAME, DB_VERSION)
//       sans callback upgrade (l'ancien getDB() de orderQueueService) exécuté
//       avant initDB() - le navigateur monte la version et ne crée rien, puis
//       initDB() ne voit plus rien à migrer. La cause est corrigée ; ce bump
//       force upgrade() à repasser pour recréer ce qui manque.
//  11 → store appVersion (contrôle de version applicatif, cf. appVersionService).
const DB_VERSION = 11;// old 10
const SECRET_KEY = process.env.VUE_APP_SECRET_KEY_INDEXDB ;

// 🔹 Stores attendus par le code. Sert au contrôle d'intégrité au boot :
//    une base à la bonne version mais amputée d'un store est le symptôme
//    exact du bug réparé par la v10, et passait jusqu'ici inaperçue.
const STORES_ATTENDUS = [
    'cachedata', 'cachedatav',
    'orderQueue', 'orderQueueTrash', 'comptabilisationvente',
    'waitNumberCounter', 'waitNumbers', 'printCounter',
    'sgSessions', 'sgCatalogueMeta', 'sgCart', 'sgDevice', 'sgArticles',
    'svIdentite', 'svCommandes',
    'appVersion',
];

// 🔹 Connexion unique par onglet.
//
//    initDB() était appelée par chaque service à chaque opération et ouvrait
//    une connexion neuve à chaque fois. Ces connexions ne se fermaient jamais
//    et bloquaient toutes l'upgrade demandé par un autre onglet - c'est ce qui
//    faisait échouer les montées de DB_VERSION en silence.
//
//    La connexion est mémoïsée ici, et le cache est vidé dès qu'elle est
//    fermée (blocking / terminated) pour que l'appel suivant en rouvre une.
let dbPromise = null;
let connexionObsolete = false;

async function initDB() {
    if (!dbPromise) {
        dbPromise = openAppDB()
            .then(db => { verifierIntegriteStores(db); return db; })
            .catch(err => {
                dbPromise = null;   // un échec ne doit pas être mis en cache
                throw err;
            });
    }
    return dbPromise;
}

// 🔹 Une base peut porter la bonne version tout en étant amputée de stores :
//    il suffit qu'un openDB(DB_NAME, DB_VERSION) SANS callback upgrade se soit
//    exécuté avant initDB(). Le navigateur monte alors la version sans rien
//    créer, et initDB() n'a plus rien à migrer. Le symptôme est un
//    NotFoundError « object store was not found » au premier accès, longtemps
//    après le boot. On le détecte ici, au moment où c'est encore diagnosticable.
//
//    Correctif : incrémenter DB_VERSION, ce qui refait passer upgrade() - //    l'opération n'étant plus destructive, elle ne coûte rien.
function verifierIntegriteStores(db) {
    const manquants = STORES_ATTENDUS.filter(s => !db.objectStoreNames.contains(s));
    if (manquants.length) {
        console.error(
            `[IndexedDB] Base v${db.version} INCOMPLÈTE - store(s) manquant(s) : ${manquants.join(', ')}. ` +
            `Toute lecture de ces stores échouera en NotFoundError. ` +
            `Correctif : incrémenter DB_VERSION pour rejouer upgrade().`
        );
    }
    return manquants;
}

// 🔹 Vrai : la connexion a été fermée au profit d'un autre onglet qui applique
//    une nouvelle version. L'onglet courant doit être rechargé.
function isConnexionObsolete() {
    return connexionObsolete;
}

async function openAppDB() {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, tx) {

            // Création idempotente. C'est ce qui rend une montée de version non
            // destructive : un store déjà présent est réutilisé tel quel, seuls
            // les manquants sont créés. Surtout, ne JAMAIS réintroduire ici une
            // boucle deleteObjectStore() : elle effaçait tout le contenu local
            // à chaque incrément de DB_VERSION.
            const ensureStore = (name, options) => {
                if (db.objectStoreNames.contains(name)) return tx.objectStore(name);
                return db.createObjectStore(name, options);
            };
            const ensureIndex = (store, name, keyPath, options) => {
                if (!store.indexNames.contains(name)) store.createIndex(name, keyPath, options);
            };

            // 1️⃣ Caches génériques
            const store = ensureStore('cachedata', { keyPath: 'id', autoIncrement: true });
            ensureIndex(store, 'cléIndex', 'clé', { unique: true });

            const storecachedatav = ensureStore('cachedatav', { keyPath: 'id', autoIncrement: true });
            ensureIndex(storecachedatav, 'cléIndex', 'clé', { unique: true });

            // 2️⃣ "orderQueue" - file d'attente des commandes
            const oqStore = ensureStore('orderQueue', { keyPath: 'id', autoIncrement: true });
            ensureIndex(oqStore, 'localIdIndex', 'localId', { unique: false });
            ensureIndex(oqStore, 'statusIndex', 'status', { unique: false });

            // 3️⃣ "orderQueueTrash" - corbeille d'audit (v9). Toute suppression
            // dans orderQueue y est archivée avant d'être effectuée, pour qu'une
            // commande effacée reste traçable et restaurable. Le payload y est
            // recopié TEL QUEL, toujours chiffré : jamais de clair sur le disque.
            const trashStore = ensureStore('orderQueueTrash', { keyPath: 'id', autoIncrement: true });
            ensureIndex(trashStore, 'deletedAtIndex', 'deletedAt', { unique: false });
            ensureIndex(trashStore, 'localIdIndex', 'localId', { unique: false });
            ensureIndex(trashStore, 'reasonIndex', 'reason', { unique: false });

            // 4️⃣ "comptabilisationvente" - ventes comptabilisées (v9).
            // Une facture par enregistrement, clé "pidcaisse|caissejour|srvdocnum".
            // Champs de filtrage en clair (convention des stores sg*/sv*), lignes
            // d'articles chiffrées dans "blob". Rétention 9 jours par caissejour.
            const cvStore = ensureStore('comptabilisationvente', { keyPath: 'k' });
            ensureIndex(cvStore, 'scopeIndex', 'scope', { unique: false });          // "pidcaisse|caissejour"
            ensureIndex(cvStore, 'caissejourIndex', 'caissejour', { unique: false }); // purge 9 jours
            ensureIndex(cvStore, 'pidcaisseIndex', 'pidcaisse', { unique: false });

            // 5️⃣ Numéros d'attente
            ensureStore('waitNumberCounter', {
                keyPath: 'datePrefix', // la date du jour sert directement de clé primaire
            });

            const waitNumbersStore = ensureStore('waitNumbers', { keyPath: 'id', autoIncrement: true });
            ensureIndex(waitNumbersStore, 'datePrefix', 'datePrefix', { unique: false });
            ensureIndex(waitNumbersStore, 'createdAt', 'createdAt', { unique: false });

            // 6️⃣ Compteurs d'impression
            ensureStore('printCounter', { keyPath: 'id', autoIncrement: true });

            // 6️⃣ Stores Scan & Go (PWA client /panier) - voir hsigscangoIndexdb.js
            // Convention : keyPath EN CLAIR (db.get direct en O(1)), valeur chiffrée
            // dans un champ "blob" quand elle est sensible. Le chiffrement CryptoJS
            // en mode passphrase n'est pas déterministe (salt + IV aléatoires), donc
            // une clé chiffrée ne peut jamais être retrouvée par index.get().
            // Ne jamais nommer un champ "clé" ici : getData/deleteData feraient un
            // decryptData(item.clé) sur ces stores.

            ensureStore('sgSessions', { keyPath: 'k' });      // "soc|mag"
            ensureStore('sgCatalogueMeta', { keyPath: 'k' }); // "soc|mag"
            ensureIndex(ensureStore('sgCart', { keyPath: 'k' }), // "soc|mag"
                'sessionIdIndex', 'sessionId', { unique: false });
            ensureStore('sgDevice', { keyPath: 'k' });        // singleton "device"

            // sgArticles : un enregistrement par article, clé "soc|mag|ean".
            // Volumineux (jusqu'à ~50 000 lignes) et SANS champ expiretime : la
            // péremption est portée par sgCatalogueMeta, et ce store est exclu de
            // cleanAllExpiredData (qui ferait un await par ligne à chaque boot).
            ensureIndex(ensureStore('sgArticles', { keyPath: 'k' }),
                'scopeIndex', 'scope', { unique: false });

            // 7️⃣ Stores du module Serveur (prise de commande en salle) - voir
            // serveurStoreDB.js. Mêmes conventions que les stores sg* ci-dessus.
            //
            // Le catalogue N'EST PAS dupliqué ici : le module serveur lit
            // sgArticles/sgCatalogueMeta, partagés avec /menu et /panier. Seules
            // les données qui lui appartiennent en propre ont un store.

            // Identité du serveur en poste. Singleton "serveur" : un seul
            // agent par téléphone, et le suivant remplace le précédent.
            ensureStore('svIdentite', { keyPath: 'k' });

            // Une commande par enregistrement, clé = son id.
            //
            // Volontairement PAS dans sgCart : ce store n'accepte qu'un seul
            // panier par magasin (clé "soc|mag"), si bien qu'une commande de
            // serveur y écraserait le panier du client Scan & Go du même
            // magasin. Ici, statut et scope restent en clair pour rester
            // filtrables par index sans tout déchiffrer.
            const svCommandes = ensureStore('svCommandes', { keyPath: 'k' });
            ensureIndex(svCommandes, 'scopeIndex', 'scope', { unique: false });
            ensureIndex(svCommandes, 'statutIndex', 'statut', { unique: false });

            // 8️⃣ "appVersion" - historique du contrôle de version applicatif
            // (v11). Singleton "app" : version installée, version visée,
            // nombre de tentatives. Voir appVersionService.js.
            //
            // Contenu NON chiffré et NON indexé : ce sont des numéros de
            // version, sans caractère sensible, et le module doit rester
            // lisible même quand la clé de chiffrement est indisponible.
            //
            // ⚠️ Ne jamais y écrire de champ "expiretime" : cleanAllExpiredData
            // parcourt tous les stores et supprimerait l'enregistrement.
            ensureStore('appVersion', { keyPath: 'k' });

        },
        // Un autre onglet tenant l'ancienne connexion empêche l'upgrade :
        // sans ce callback, openDB reste en attente indéfiniment et le boot
        // gèle sur une page blanche. On trace au lieu de geler.
        blocked() {
            console.warn('[IndexedDB] Ouverture bloquée par un autre onglet ouvert');
        },
        // Symétrique de blocked() : c'est NOTRE connexion qui empêche un autre
        // onglet d'appliquer une nouvelle version. On la ferme pour le laisser
        // passer - sans quoi son upgrade reste bloqué indéfiniment.
        //
        // Conséquence assumée : la base devient inutilisable dans cet onglet,
        // qui doit être rechargé. On ne recharge pas d'office, ce qui couperait
        // une saisie de caisse en cours ; l'événement ci-dessous permet de le
        // signaler à l'utilisateur, qui recharge quand il a terminé.
        blocking(currentVersion, blockedVersion, event) {
            console.warn(`[IndexedDB] Upgrade v${currentVersion} → v${blockedVersion} demandé par un autre onglet - fermeture de la connexion`);
            try { event.target.close(); } catch (e) { /* déjà fermée */ }
            dbPromise = null;
            connexionObsolete = true;
            try {
                window.dispatchEvent(new CustomEvent('hsig:db-obsolete', {
                    detail: { from: currentVersion, to: blockedVersion },
                }));
            } catch (e) { /* environnement sans window */ }
        },
        // La connexion a été fermée par le navigateur (ex. base supprimée
        // ailleurs, mémoire) : on trace et on vide le cache pour que l'appel
        // suivant en rouvre une.
        terminated() {
            console.error('[IndexedDB] Connexion IndexedDB terminée par le navigateur');
            dbPromise = null;
        },
    });
}

function resetDatabase() {
    // Supprimer la base
    const deleteRequest = indexedDB.deleteDatabase(DB_NAME);

    deleteRequest.onsuccess = function() {
        console.log("Base supprimée. Recréation en cours...");
        initDB(); // Appeler la fonction pour recréer la base
    };

    deleteRequest.onerror = function() {
        console.log("Erreur lors de la suppression de la base.");
    };
}

// 🔹 Lire la version réelle de la base présente dans le navigateur.
//    Renvoie null si la base n'existe pas encore.
function readBrowserDBVersion() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB.databases === 'function') {
            indexedDB.databases()
                .then(dbs => {
                    const found = dbs.find(d => d.name === DB_NAME);
                    resolve(found ? found.version : null);
                })
                .catch(reject);
            return;
        }

        // Repli Firefox (pas d'indexedDB.databases) : ouvrir sans numéro de
        // version renvoie la version courante, mais CRÉE la base en version 1
        // si elle est absente. onupgradeneeded ne se déclenche que dans ce cas :
        // il sert donc à repérer la base fantôme pour la supprimer aussitôt.
        let wascreated = false;
        const request = indexedDB.open(DB_NAME);

        request.onupgradeneeded = function() {
            wascreated = true;
        };

        request.onsuccess = function() {
            const version = request.result.version;
            request.result.close();

            if (wascreated) {
                indexedDB.deleteDatabase(DB_NAME);
                resolve(null);
                return;
            }
            resolve(version);
        };

        request.onerror = function() {
            reject(request.error);
        };
    });
}

// 🔹 deleteDatabase attendable. resetDatabase() ci-dessus est en callbacks
//    et ne traite pas onblocked : impossible de l'enchaîner de façon fiable.
function deleteDatabasePromise() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME);

        request.onsuccess = function() {
            resolve();
        };

        request.onerror = function() {
            reject(request.error);
        };

        // Un autre onglet gardant une connexion ouverte bloque la suppression
        // indéfiniment : sans ce handler, le boot gèle en silence.
        request.onblocked = function() {
            reject(new Error(
                "Suppression de la base bloquée : l'application est ouverte dans un autre onglet. " +
                "Fermez les autres onglets puis rechargez."
            ));
        };
    });
}

// 🔹 Compte les commandes non synchronisées (même définition que
//    orderQueueService.getQueueCount). Lecture directe via idb : importer
//    orderQueueService ici créerait un cycle, ce module étant son import.
async function countUnsyncedOrders() {
    const db = await openDB(DB_NAME);
    try {
        if (!db.objectStoreNames.contains('orderQueue')) return 0;
        const all = await db.getAll('orderQueue');
        return all.filter(r => r.status === 'pending' || r.status === 'failed').length;
    } finally {
        db.close();
    }
}

async function hardResetAll() {
    try {
        const nb = await countUnsyncedOrders();
        if (nb > 0) {
            console.error(`[DB PURGE] ${nb} commande(s) non synchronisée(s) définitivement détruite(s)`);
        }
    } catch (e) {
        // Schéma divergent ou base illisible : on trace sans bloquer la purge.
        console.warn('[DB PURGE] file de commandes illisible avant purge', e);
    }

    await deleteDatabasePromise();

    window.localStorage.clear();
    window.sessionStorage.clear();
}

// 🔹 Compare la version de la base présente dans le navigateur à la constante
//    DB_VERSION du code.
//
//    Base PLUS ANCIENNE que le code : migration normale. On ne purge pas - //    upgrade() est incrémental depuis la v9 et ne crée que les stores manquants.
//    C'est le cas courant après un déploiement, et purger ici détruisait les
//    commandes en attente de tout le parc à chaque incrément de version.
//
//    Base PLUS RÉCENTE que le code : le poste exécute un bundle antérieur au
//    schéma déjà en place (rollback, service worker périmé). IndexedDB refuse
//    d'ouvrir en version inférieure et lèverait un VersionError : la purge reste
//    la seule issue. C'est désormais le seul cas destructif.
async function forceDBVersionCheck() {
    const browserVersion = await readBrowserDBVersion();

    if (browserVersion === null) return { purged: false, reason: 'nouvelle-installation' };
    if (browserVersion === DB_VERSION) return { purged: false, reason: 'a-jour' };

    if (browserVersion < DB_VERSION) {
        return { purged: false, reason: 'migration', from: browserVersion, to: DB_VERSION };
    }

    console.warn(`[DB] Base v${browserVersion} plus récente que le code v${DB_VERSION} - purge`);
    await hardResetAll();
    return { purged: true, from: browserVersion, to: DB_VERSION };
}

 /*async function forceDBVersionCheck() {
    const browserVersion = await readBrowserDBVersion();

    // Base absente : première installation, rien à purger.
    if (browserVersion === null) return { purged: false, reason: 'nouvelle-installation' };
    if (browserVersion === DB_VERSION) return { purged: false, reason: 'a-jour' };

    await hardResetAll();
    return { purged: true, from: browserVersion, to: DB_VERSION };
}*/

// 🔹 Chiffrer les données avant stockage
function encryptData(data) {
    //return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();


    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
      return encrypted;




}


// 🔹 Déchiffrer les données avant lecture

function decryptData(encryptedData) {
    try {

        //console.log("SECRET_KEY ",SECRET_KEY)

        // Déchiffrement des données
        const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);

        //console.log("bytes ******** ",bytes)

        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        //console.log("decrypted ******** ",decrypted)
        // Vérifier si la donnée déchiffrée est valide
        if (!decrypted || decrypted.trim() === "") {
            console.error("Donnée déchiffrée vide ou invalide");
            return null;
        }

        // Essayer de parser la chaîne déchiffrée en JSON
        try {
            return JSON.parse(decrypted);
        } catch (parseError) {
            console.error("Erreur de parsing JSON :", parseError);
            return null;
        }
    } catch (error) {
        console.error("Erreur de déchiffrement :", error);
        return null;
    }
}

// 🔹 Ajouter ou mettre à jour une donnée dans une table
/*
async function saveDataold(table, mclé, value) {

    const expiretime=Date.now() + 24 * 3600 * 1000 //24h soit 1 jour

    const db = await initDB();
    const encryptedValue = encryptData(value);
    const encryptedclé = encryptData(mclé);

   // console.log("Encrypted value to store:", encryptedValue); // Vérification avant le stockage


   // const data = { clé, value: encryptedValue, expiretime };
    const data = { clé:encryptedclé, value: encryptedValue, expiretime };

    await db.put(table, data);
}
*/

/*
async function saveData(table, mclé, value) {
    const expiretime = Date.now() + 24 * 3600 * 1000; // 24h soit 1 jour
    const db = await initDB();

    const encryptedValue = encryptData(value);
    const encryptedclé = encryptData(mclé);

    // Ouvrir une transaction en lecture/écriture
    const tx = db.transaction(table, "readwrite");
    const store = tx.objectStore(table);
    const index = store.index("cléIndex"); // Utiliser l'index secondaire

    // Vérifier si la clé existe déjà
    const request = index.get(encryptedclé);

    request.onsuccess = async function () {
        const existingData = request.result;
        let data;

        if (existingData) {
            // Mise à jour de l'enregistrement existant
            data = { ...existingData, value: encryptedValue, expiretime };
        } else {
            // Nouvelle insertion avec une clé auto-incrémentée
            data = { clé: encryptedclé, value: encryptedValue, expiretime };
        }



        // Sauvegarde dans IndexedDB
        store.put(data);
    };

    request.onerror = function () {
        console.error("Erreur lors de la récupération de la clé:", request.error);
    };

    await tx.complete;
}
*/

async function saveData(table, mclé, value) {
    const expiretime = Date.now() + 24 * 3600 * 1000; // 24h soit 1 jour
    const db = await initDB();
console.log("1")
    const encryptedValue = encryptData(value);
    const encryptedclé = encryptData(mclé);

    // Ouvrir une transaction en lecture/écriture
    const tx = db.transaction(table, "readwrite");
    const store = tx.objectStore(table);
   // console.log("2")
    // Vérifier si l'index existe et l'ajouter si nécessaire
    const index = store.index("cléIndex");
    //console.log("3")
    // Nouvelle promesse pour gérer les erreurs et la réussite
    return new Promise((resolve, reject) => {
        const request = index.get(encryptedclé);
       // console.log("4")
        request.onsuccess = async function () {
            const existingData = request.result;
            let data;

            if (existingData) {
                // Mise à jour de l'enregistrement existant
                data = { ...existingData, value: encryptedValue, expiretime };
            } else {
                // Nouvelle insertion avec une clé auto-incrémentée
                data = { clé: encryptedclé, value: encryptedValue, expiretime };
            }

            // Sauvegarde dans IndexedDB
            const putRequest = store.put(data);

            putRequest.onsuccess = function () {
                resolve('Données sauvegardées avec succès');
            };

            putRequest.onerror = function () {
                reject('Erreur lors de la sauvegarde des données');
            };
        };

        request.onerror = function () {
            reject('Erreur lors de la récupération de la clé');
        };
    });
}

async function saveOrUpdateData(table, clé, value) {
    const db = await initDB();
    const tx = db.transaction(table, "readwrite");
    const store = tx.objectStore(table);

    const allData = await store.getAll();


    const existingData = allData.find(item => decryptData(item.clé) === clé);

    let data = {
        clé: encryptData(clé),
        value: encryptData(value),
        expiretime: Date.now() + 24 * 3600 * 1000 // Expiration dans 12 heures
    };

    if (existingData) {
        // Mise à jour de la donnée existante
        data = { ...existingData, value: encryptData(value), expiretime: Date.now() + 24 * 3600 * 1000 };
        store.put(data); // Met à jour la donnée
    } else {
        // Insertion de nouvelle donnée
        store.add(data); // Ajoute la nouvelle donnée
    }

    await tx.complete;
}


// 🔹 Lire une donnée avec vérification de l'expiration
/*
async function getDataold(table, clé) {
    const db = await initDB();
    console.log("data cache table ----------- ",table)
    console.log("data cache clé ----------- ",clé)
    const encryptedclé = encryptData(clé);

    //const data = await db.get(table, clé);
    const data = await db.get(table, encryptedclé);

    console.log("data cache ----------- ",data)

    if (!data) return [];

    // Vérifier si la donnée est expirée
    if (data.expiretime && Date.now() > data.expiretime) {
        //await deleteData(table, clé);
        await deleteData(table, encryptedclé);
        return null;
    }

    return decryptData(data.value);
}
*/
async function getData(table, clé) {

    const db = await initDB();

    const tx = db.transaction(table, "readonly");
    const store = tx.objectStore(table);
    const allData = await store.getAll();



  /*  for (let $line of allData){

        console.log("$line ********** ",$line)
        console.log("$line.clé ********** ",$line.clé)
        console.log("decryptData $line.clé ********** ",decryptData($line.clé))

    }*/



/*
    console.log("Toutes les données:", allData);
    console.log("encryptedclé:", encryptedclé);
    console.log("decryptData+encryptedclé:", decryptData(encryptedclé));
*/
    const data = allData.find(item => decryptData(item.clé) === clé);
    if (data) {
       // console.log("Donnée trouvée:", data);
        if (data.expiretime && Date.now() > data.expiretime) {
            //console.log("La donnée a expiré");
            return []; // Donnée expirée
        }

        //console.log("data ********* ",data);
        //console.log("data.value ********* ",data.value);

        return decryptData(data.value); // Retourne la valeur décryptée
    }
    else{
        //console.log("Clé non trouvée");
        return []; // Clé non trouvée
    }



}

// 🔹 Supprimer une donnée
async function deleteData(table, clé) {
    const db = await initDB();

    const tx = db.transaction(table, "readwrite"); // Mode readwrite pour suppression
    const store = tx.objectStore(table);

    const allData = await store.getAll(); // Attendre la récupération des données
    const data = allData.find(item => decryptData(item.clé) === clé);

    if (data) {
        await store.delete(data.id); // Suppression avec la bonne clé (ajustez selon votre structure)
    }

    await tx.complete; // S'assurer que la transaction est bien terminée
}


// 🔹 Supprimer les données expirées
/*
async function cleanExpiredDataold(table) {
    const db = await initDB();
    const allKeys = await db.getAllKeys(table);
    for (const key of allKeys) {
        const data = await db.get(table, key);
        if (data.expiretime && Date.now() > data.expiretime) {
            await db.delete(table, key);
        }
    }
}
*/
// 🔹 Supprimer les données expirées plus efficacement
async function cleanExpiredDataold(table) {
    const db = await initDB();
    const tx = db.transaction(table, "readwrite");
    const store = tx.objectStore(table);

    // Utilisation d'un curseur pour parcourir les enregistrements
    const request = store.openCursor();

    request.onsuccess = function (event) {
        const cursor = event.target.result;
        if (cursor) {
            const data = cursor.value;
            if (data.expiretime && Date.now() > data.expiretime) {
                console.log(`🔹 Suppression de la clé expirée: ${data.clé}`);
                cursor.delete(); // Suppression directe via le curseur
            }
            cursor.continue(); // Passer à l'enregistrement suivant
        }
    };

    request.onerror = function () {
        console.error("Erreur lors du nettoyage des données expirées:", request.error);
    };

    await tx.complete;
}

async function cleanExpiredDataInStore(storeName) {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        let cursor = await store.openCursor();
        let deletedCount = 0;

        while (cursor) {
            const { value } = cursor;
            if (value.expiretime && Date.now() > value.expiretime) {
               // console.log(`🗑️ Suppression expirée dans [${storeName}]: clé=${value.clé || value.id}`);
                await cursor.delete();
                deletedCount++;
            }
            cursor = await cursor.continue();
        }

        await tx.done;
        console.log(`✅ Nettoyage terminé pour [${storeName}] : ${deletedCount} supprimé(s)`);
    } catch (error) {
        console.error(`❌ Erreur lors du nettoyage du store [${storeName}]:`, error);
    }
}

async function cleanAllExpiredData() {
    try {
        const db = await initDB();

        // sgArticles est exclu : le nettoyage ouvre un curseur avec un await par
        // enregistrement, et ce store peut contenir ~50 000 articles. Le parcourir
        // à chaque boot (avant createApp) ralentirait toute l'app, caisse comprise.
        // Ses lignes n'ont d'ailleurs pas d'expiretime : leur péremption est portée
        // par sgCatalogueMeta, et hsigscangoIndexdb les purge en masse par scope.
        const storeNames = Array.from(db.objectStoreNames).filter(name => name !== 'sgArticles');

       /// console.log(`🧹 Début du nettoyage pour tous les stores :`, storeNames);

        for (const storeName of storeNames) {
            await cleanExpiredDataInStore(storeName);
        }

        console.log('✅ Nettoyage global terminé !');
    } catch (error) {
        console.error('❌ Erreur lors du nettoyage global :', error);
    }
}



// 🔹 Récupérer toutes les données d'une table
/*
async function getAllDataold(table) {
    const db = await initDB();
    const data = await db.getAll(table);
    return data.map(item => ({
        clé: item.clé,
        value: decryptData(item.value),
        expiretime: item.expiretime
    }));
}
*/
async function getAllData(table) {
    const db = await initDB();
    const tx = db.transaction(table, "readonly");
    const store = tx.objectStore(table);

    const request = store.getAll();

    return new Promise((resolve, reject) => {
        request.onsuccess = function () {
            const result = request.result.map(item => ({
                clé: item.clé, // La clé reste chiffrée, veux-tu la déchiffrer ?
                value: decryptData(item.value),
                expiretime: item.expiretime
            }));
            resolve(result);
        };
        request.onerror = function () {
            console.error("Erreur lors de la récupération des données:", request.error);
            reject(request.error);
        };
    });
}


/************************ Gérer plusieur store *****************************/
/*
export async function addDatabyStore(storeName, data) {
    const db = await initDB();
    const tx = db.transaction(storeName, 'readwrite');
    await tx.store.add(data);
    await tx.done;
    console.log(`✅ Ajouté dans [${storeName}]`);
}

export async function getAllDatabyStore(storeName) {
    const db = await initDB();
    return await db.getAll(storeName);
}

export async function deleteDatabyStore(storeName, id) {
    const db = await initDB();
    await db.delete(storeName, id);
    console.log(`🗑️ Supprimé id=${id} de [${storeName}]`);
}

export async function updateDatabyStore(storeName, data) {
    const db = await initDB();
    const tx = db.transaction(storeName, 'readwrite');
    await tx.store.put(data);
    await tx.done;
    console.log(`🔄 Mis à jour dans [${storeName}]`);
}

//exemple d'utilisation

(async () => {
    await initDB();

    // Exemple ➜ Ajouter dans cachedata
    await addDatabyStore('cachedata', { clé: 'userSession', expiretime: Date.now() + 3600000 });

    // Exemple ➜ Ajouter dans tableaudebord
    await addDatabyStore('tableaudebord', { titre: 'Evolution des ventes', données: [1, 2, 3] });

    // Exemple ➜ Ajouter dans libelle
    await addDatabyStore('libelle', { code: 'FR', texte: 'Bonjour' });

    // Lire tout
    const allCache = await getAllData('cachedata');
    console.log('📦 cachedata', allCache);
})();
*/

export { saveData, getData, deleteData, getAllData,
    resetDatabase,saveOrUpdateData,initDB,cleanAllExpiredData,
    cleanExpiredDataold,forceDBVersionCheck,hardResetAll,
    encryptData, decryptData, DB_NAME, DB_VERSION,
    isConnexionObsolete
};

